import { useMemo } from 'react';

import { BLOCKING_BEHAVIOR, FraudSeverity, type BlockingBehavior } from '../../../constants/fraud-constants';
import { emptyArray } from 'ui-components/default-props/empty';

import type { FraudDetectionData, FraudIndicator } from '../../../types/fraud-detection';

type FulfillmentPlan = {
  fraudDetection?: FraudDetectionData | null;
  [key: string]: unknown;
};

type UseFraudIndicatorsReturn = {
  indicators: FraudIndicator[];
  hasAnyIndicators: boolean;
  hasBlockingIndicators: boolean;
  hasWarningIndicators: boolean;
  hasInfoIndicators: boolean;
  blockingIndicators: FraudIndicator[];
  warningIndicators: FraudIndicator[];
  infoIndicators: FraudIndicator[];
};

/**
 * Maps backend fraud severity to frontend BlockingBehavior constant
 */
const mapSeverityToBlockingBehavior = (severity: number, blocksShipping: boolean): BlockingBehavior => {
  if (blocksShipping || severity >= FraudSeverity.High) {
    return BLOCKING_BEHAVIOR.BLOCKS;
  }

  if (severity === FraudSeverity.Warning) {
    return BLOCKING_BEHAVIOR.FLAGS;
  }

  return BLOCKING_BEHAVIOR.INFO;
};

/**
 * Hook to extract and process fraud indicators from fulfillmentPlan.fraudDetection
 */
export const useFraudIndicators = (fulfillmentPlan?: FulfillmentPlan | null): UseFraudIndicatorsReturn => {
  const fraudData = useMemo(() => {
    // Handle missing fulfillmentPlan
    if (!fulfillmentPlan) {
      return emptyArray as FraudIndicator[];
    }

    const { fraudDetection } = fulfillmentPlan;

    // Handle missing fraudDetection property
    if (!fraudDetection) {
      return emptyArray as FraudIndicator[];
    }

    // Handle missing or invalid indicators array
    if (!Array.isArray(fraudDetection.indicators)) {
      return emptyArray as FraudIndicator[];
    }

    // Map indicators to include computed blockingBehavior
    return fraudDetection.indicators.map((indicator) => ({
      fraudIndicatorCode: indicator.fraudIndicatorCode,
      fraudIndicatorName: indicator.fraudIndicatorName,
      category: indicator.category,
      appliedDateUtc: indicator.appliedDateUtc,
      blocksShipping: indicator.blocksShipping,
      severity: indicator.severity,
      blockingBehavior: mapSeverityToBlockingBehavior(indicator.severity, indicator.blocksShipping),
      metadata: indicator.metadata ?? null,
    }));
  }, [fulfillmentPlan]);

  const computed = useMemo(() => {
    if (fraudData.length === 0) {
      return {
        hasAnyIndicators: false,
        hasBlockingIndicators: false,
        hasWarningIndicators: false,
        hasInfoIndicators: false,
        blockingIndicators: emptyArray as FraudIndicator[],
        warningIndicators: emptyArray as FraudIndicator[],
        infoIndicators: emptyArray as FraudIndicator[],
      };
    }

    // Group indicators by blocking behavior
    const blockingIndicators = fraudData.filter((ind) => ind.blockingBehavior === BLOCKING_BEHAVIOR.BLOCKS);
    const warningIndicators = fraudData.filter((ind) => ind.blockingBehavior === BLOCKING_BEHAVIOR.FLAGS);
    const infoIndicators = fraudData.filter((ind) => ind.blockingBehavior === BLOCKING_BEHAVIOR.INFO);

    return {
      hasAnyIndicators: true,
      hasBlockingIndicators: blockingIndicators.length > 0,
      hasWarningIndicators: warningIndicators.length > 0,
      hasInfoIndicators: infoIndicators.length > 0,
      blockingIndicators,
      warningIndicators,
      infoIndicators,
    };
  }, [fraudData]);

  return {
    indicators: fraudData,
    ...computed,
  };
};
